(function () {
  const VISIBLE_BATCH_SIZE = 12;
  const MOBILE_QUERY = "(max-width: 749px)";
  const HISTORY_KEY = "printmoGarmentCollection";
  const SORT_PARAM = "pmc_sort";

  const asArray = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
  };

  const normalize = (value) => String(value || "").trim().toLowerCase();

  const cloneFilters = (filters) => {
    const cloned = new Map();
    filters.forEach((values, key) => cloned.set(key, new Set(values)));
    return cloned;
  };

  const normalizeLegacyDetail = (value) => {
    const supported = {
      "garment dyed": "Garment-dyed",
      "moisture-wicking": "Moisture-wicking",
      pocket: "Pocket",
      "tear-away tag": "Tear-away tag",
      preshrunk: "Preshrunk",
    };
    return supported[normalize(value)] || null;
  };

  const sizeAvailability = (sizes) => {
    const normalized = new Set(asArray(sizes).map(normalize));
    const has = (...values) => values.some((value) => normalized.has(normalize(value)));
    return [
      has("XS", "Extra Small") ? "XS available" : null,
      has("3XL") ? "3XL available" : null,
      has("4XL") ? "4XL available" : null,
      has("5XL") ? "5XL available" : null,
    ].filter(Boolean);
  };

  const productFacetValues = (product, key, facet) => {
    switch (key) {
      case "best_for":
        return asArray(product.bestFor);
      case "feel_weight":
        return [product.feel, product.weight].filter(Boolean);
      case "style":
        return asArray(product.style);
      case "fit":
        return asArray(product.fit);
      case "material":
        return asArray(product.material?.length ? product.material : product.legacyMaterial);
      case "price":
        return asArray(facet?.bands)
          .filter((band) => {
            const minimumMatches = band.minimum === null || band.minimum === undefined || product.price >= band.minimum;
            const maximumMatches = band.maximum === null || band.maximum === undefined || product.price < band.maximum;
            return minimumMatches && maximumMatches;
          })
          .map((band) => band.label);
      case "size_availability":
        return sizeAvailability(product.sizes);
      case "color":
        return asArray(product.colors);
      case "brand":
        return asArray(product.brand);
      case "more_details":
        return [
          ...asArray(product.details),
          ...asArray(product.legacyDetails).map(normalizeLegacyDetail).filter(Boolean),
        ];
      default:
        return [];
    }
  };

  class GarmentCollection {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector("[data-catalog-grid]");
      this.dataElement = root.querySelector("[data-catalog-data]");
      this.filterGroups = root.querySelector("[data-filter-groups]");
      this.filterShell = root.querySelector("[data-filters-shell]");
      this.sort = root.querySelector("[data-catalog-sort]");
      this.resultCount = root.querySelector("[data-result-count]");
      this.liveStatus = root.querySelector("[data-result-status]");
      this.applied = root.querySelector("[data-applied-filters]");
      this.clearButton = root.querySelector("[data-clear-filters]");
      this.emptyState = root.querySelector("[data-empty-state]");
      this.emptyClearButton = root.querySelector("[data-empty-clear]");
      this.loadMoreButton = root.querySelector("[data-load-more]");
      this.updatingStatus = root.querySelector("[data-updating-status]");
      this.errorTemplate = root.querySelector("[data-catalog-error-template]");
      this.errorState = null;
      this.refreshButton = null;
      this.mobileFilterButton = root.querySelector("[data-mobile-filter-open]");
      this.mobileSortButton = root.querySelector("[data-mobile-sort-open]");
      this.mobileFilterCount = root.querySelector("[data-mobile-filter-count]");
      this.mobileSortLabel = root.querySelector("[data-mobile-sort-label]");
      this.filterDialog = root.querySelector("[data-filter-dialog]");
      this.sortDialog = root.querySelector("[data-sort-dialog]");
      this.mobileFilterHost = root.querySelector("[data-mobile-filter-host]");
      this.mobileResultCount = root.querySelector("[data-mobile-result-count]");
      this.mobileSortOptions = root.querySelector("[data-mobile-sort-options]");
      this.activeFilters = new Map();
      this.pendingFilters = new Map();
      this.visibleLimit = VISIBLE_BATCH_SIZE;
      this.products = [];
      this.facets = [];
      this.facetValues = new Map();
      this.cardById = new Map();
      this.mobileExpandedFacets = new Set();
      this.filterOrigin = null;
      this.lastDialogTrigger = null;
      this.isApplyingDialog = false;
      this.scrollTimer = null;
      this.mobileQuery = typeof window.matchMedia === "function"
        ? window.matchMedia(MOBILE_QUERY)
        : { matches: false, addEventListener() {} };

      if (!this.grid || !this.dataElement) return;

      try {
        const data = JSON.parse(this.dataElement.textContent || "{}");
        this.products = asArray(data.products);
        this.facets = asArray(data.facets);
      } catch (error) {
        this.showError();
        return;
      }

      this.grid.querySelectorAll(".pmc-card[data-product-id]").forEach((card) => {
        this.cardById.set(String(card.dataset.productId), card);
      });

      this.buildFilters();
      this.readUrlState();
      this.syncFilterControls(this.activeFilters);
      this.bindEvents();
      this.adaptToViewport();
      this.restoreInterfaceState(window.history.state, true);
      this.render(false);
      this.updateContextLinks();
      this.root.dataset.catalogEnhanced = "true";
    }

    buildFilters() {
      if (!this.filterGroups) return;
      const fragment = document.createDocumentFragment();

      this.facets.forEach((facet) => {
        if (!facet.enabled) return;
        const counts = new Map();
        this.products.forEach((product) => {
          new Set(productFacetValues(product, facet.key, facet)).forEach((value) => {
            counts.set(value, (counts.get(value) || 0) + 1);
          });
        });

        const configuredOrder = new Map(asArray(facet.values).map((value, index) => [value, index]));
        const values = [...counts.keys()]
          .filter((value) => counts.get(value) < this.products.length)
          .filter((value) => !facet.strict || configuredOrder.has(value))
          .sort((a, b) => {
            const aOrder = configuredOrder.has(a) ? configuredOrder.get(a) : Number.MAX_SAFE_INTEGER;
            const bOrder = configuredOrder.has(b) ? configuredOrder.get(b) : Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder || a.localeCompare(b);
          });

        if (values.length < 2) return;
        this.facetValues.set(facet.key, new Set(values));

        const group = document.createElement("details");
        group.className = "pmc__filter-group";
        group.dataset.filterGroup = facet.key;
        group.open = false;

        const summary = document.createElement("summary");
        summary.className = "pmc__filter-group-summary";
        summary.textContent = facet.label;

        const fieldset = document.createElement("fieldset");
        fieldset.className = "pmc__filter-options";
        const legend = document.createElement("legend");
        legend.className = "visually-hidden";
        legend.textContent = facet.label;
        fieldset.appendChild(legend);

        values.forEach((value, index) => {
          const label = document.createElement("label");
          label.className = "pmc__filter-option";
          const input = document.createElement("input");
          input.type = "checkbox";
          input.value = value;
          input.dataset.facetKey = facet.key;
          input.id = `${this.root.id}-${facet.key}-${index}`;
          const text = document.createElement("span");
          text.textContent = value;
          const count = document.createElement("span");
          count.className = "pmc__filter-count";
          count.textContent = String(counts.get(value));
          label.append(input, text, count);
          fieldset.appendChild(label);
        });

        group.append(summary, fieldset);
        fragment.appendChild(group);
      });

      this.filterGroups.replaceChildren(fragment);
      if (this.filterShell && !this.filterGroups.children.length) this.filterShell.hidden = true;
      this.filterOrigin = document.createComment("filter-groups");
      this.filterGroups.before(this.filterOrigin);
    }

    bindEvents() {
      this.filterGroups?.addEventListener("change", (event) => {
        if (!event.target.closest("input[data-facet-key]")) return;
        const selected = this.filtersFromControls();
        if (this.isDialogOpen(this.filterDialog) && this.mobileQuery.matches) {
          this.pendingFilters = selected;
          this.updateMobileFilterPreview();
          return;
        }
        this.activeFilters = selected;
        this.visibleLimit = VISIBLE_BATCH_SIZE;
        this.commitState(true);
        this.render(true);
      });

      this.filterGroups?.addEventListener("toggle", (event) => {
        const group = event.target.closest?.("[data-filter-group]");
        if (!group || !this.mobileQuery.matches) return;
        if (group.open) this.mobileExpandedFacets.add(group.dataset.filterGroup);
        else this.mobileExpandedFacets.delete(group.dataset.filterGroup);
        this.saveInterfaceState();
      }, true);

      this.sort?.addEventListener("change", () => {
        this.visibleLimit = VISIBLE_BATCH_SIZE;
        this.commitState(true);
        this.render(true, "Product order updated.");
      });

      this.clearButton?.addEventListener("click", () => this.clearFilters(true));
      this.emptyClearButton?.addEventListener("click", () => this.clearFilters(true));
      this.loadMoreButton?.addEventListener("click", () => {
        this.visibleLimit += VISIBLE_BATCH_SIZE;
        this.render(false);
      });
      this.refreshButton?.addEventListener("click", () => window.location.reload());

      this.mobileFilterButton?.addEventListener("click", () => this.openFilterDialog());
      this.root.querySelector("[data-filter-close]")?.addEventListener("click", () => this.closeFilterDialog(false));
      this.root.querySelector("[data-mobile-clear-filters]")?.addEventListener("click", () => {
        this.pendingFilters.clear();
        this.syncFilterControls(this.pendingFilters);
        this.updateMobileFilterPreview();
      });
      this.root.querySelector("[data-mobile-apply-filters]")?.addEventListener("click", () => {
        this.activeFilters = cloneFilters(this.pendingFilters);
        this.visibleLimit = VISIBLE_BATCH_SIZE;
        this.isApplyingDialog = true;
        this.closeDialog(this.filterDialog);
        this.isApplyingDialog = false;
        this.commitState(true);
        this.render(true);
      });

      this.mobileSortButton?.addEventListener("click", () => this.openSortDialog());
      this.root.querySelector("[data-sort-close]")?.addEventListener("click", () => this.closeSortDialog());
      this.root.querySelector("[data-mobile-apply-sort]")?.addEventListener("click", () => {
        const selected = this.mobileSortOptions?.querySelector("input:checked");
        if (selected && this.sort) this.sort.value = selected.value;
        this.closeDialog(this.sortDialog);
        this.visibleLimit = VISIBLE_BATCH_SIZE;
        this.commitState(true);
        this.render(true, "Product order updated.");
      });

      [this.filterDialog, this.sortDialog].forEach((dialog) => {
        dialog?.addEventListener("cancel", (event) => {
          event.preventDefault();
          if (dialog === this.filterDialog) this.closeFilterDialog(false);
          else this.closeSortDialog();
        });
        dialog?.addEventListener("close", () => {
          if (dialog === this.filterDialog && !this.isApplyingDialog) {
            this.syncFilterControls(this.activeFilters);
          }
          this.returnDialogFocus();
        });
        dialog?.addEventListener("keydown", (event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            if (dialog === this.filterDialog) this.closeFilterDialog(false);
            else this.closeSortDialog();
            return;
          }
          this.trapFallbackFocus(event, dialog);
        });
      });

      this.root.addEventListener("click", (event) => {
        const swatch = event.target.closest("[data-swatch-group] button[data-color-name]");
        if (swatch) {
          this.selectSwatch(swatch, true);
          return;
        }
        if (event.target.closest(".pmc-card a")) this.saveInterfaceState();
      });
      this.root.addEventListener("keydown", (event) => this.handleSwatchKeys(event));
      this.root.querySelectorAll("[data-card-image]").forEach((image) => {
        image.addEventListener("error", () => image.closest(".pmc-card__media")?.classList.add("pmc-card__media--failed"));
      });

      const onViewportChange = () => this.adaptToViewport();
      if (typeof this.mobileQuery.addEventListener === "function") {
        this.mobileQuery.addEventListener("change", onViewportChange);
      } else if (typeof this.mobileQuery.addListener === "function") {
        this.mobileQuery.addListener(onViewportChange);
      }

      window.addEventListener("popstate", (event) => {
        this.readUrlState();
        this.syncFilterControls(this.activeFilters);
        this.visibleLimit = VISIBLE_BATCH_SIZE;
        this.render(true);
        this.restoreInterfaceState(event.state, true);
      });
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) this.restoreInterfaceState(window.history.state, true);
      });
      window.addEventListener("pagehide", () => this.saveInterfaceState());
      window.addEventListener("scroll", () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.saveInterfaceState(), 180);
      }, { passive: true });
    }

    filtersFromControls() {
      const selected = new Map();
      this.filterGroups?.querySelectorAll("input[data-facet-key]:checked").forEach((input) => {
        const values = selected.get(input.dataset.facetKey) || new Set();
        values.add(input.value);
        selected.set(input.dataset.facetKey, values);
      });
      return selected;
    }

    syncFilterControls(filters) {
      this.filterGroups?.querySelectorAll("input[data-facet-key]").forEach((input) => {
        input.checked = filters.get(input.dataset.facetKey)?.has(input.value) || false;
      });
      this.updateFilterGroupCounts(filters);
    }

    updateFilterGroupCounts(filters) {
      this.filterGroups?.querySelectorAll("[data-filter-group]").forEach((group) => {
        const count = filters.get(group.dataset.filterGroup)?.size || 0;
        const summary = group.querySelector(".pmc__filter-group-summary");
        if (!summary) return;
        const base = this.facets.find((facet) => facet.key === group.dataset.filterGroup)?.label || "Filter";
        summary.textContent = count ? `${base} (${count})` : base;
      });
    }

    clearFilters(pushHistory) {
      this.activeFilters.clear();
      this.syncFilterControls(this.activeFilters);
      this.visibleLimit = VISIBLE_BATCH_SIZE;
      this.commitState(pushHistory);
      this.render(true);
    }

    filteredProducts(filters = this.activeFilters, sortValue = this.sort?.value || "recommended") {
      let products = this.products.filter((product) => {
        return [...filters.entries()].every(([key, selected]) => {
          const facet = this.facets.find((item) => item.key === key);
          const values = new Set(productFacetValues(product, key, facet));
          return [...selected].some((value) => values.has(value));
        });
      });

      if (sortValue === "price-asc") products = [...products].sort((a, b) => a.price - b.price);
      if (sortValue === "price-desc") products = [...products].sort((a, b) => b.price - a.price);
      if (sortValue === "colors-desc") products = [...products].sort((a, b) => b.colorCount - a.colorCount);
      if (sortValue === "sizes-desc") products = [...products].sort((a, b) => b.sizeCount - a.sizeCount);
      return products;
    }

    render(announce, announcement) {
      this.setUpdating(true);
      const products = this.filteredProducts();
      const matchedIds = new Set(products.map((product) => String(product.id)));
      const visibleIds = new Set(products.slice(0, this.visibleLimit).map((product) => String(product.id)));

      products.forEach((product) => {
        const card = this.cardById.get(String(product.id));
        if (!card) return;
        this.grid.appendChild(card);
        card.hidden = !visibleIds.has(String(product.id));
      });
      this.cardById.forEach((card, id) => {
        if (!matchedIds.has(id)) card.hidden = true;
      });

      const noun = products.length === 1 ? this.root.dataset.singularLabel : this.root.dataset.pluralLabel;
      const message = this.activeFilters.size
        ? `${products.length} ${noun} ${products.length === 1 ? "matches" : "match"} your filters`
        : `${products.length} ${noun}`;
      if (this.resultCount) this.resultCount.textContent = message;
      if (this.emptyState) this.emptyState.hidden = products.length !== 0;
      if (this.loadMoreButton) this.loadMoreButton.hidden = products.length <= this.visibleLimit;
      this.renderAppliedFilters();
      this.updateMobileControls();
      this.updateContextLinks();
      this.setUpdating(false);

      if (announce && this.liveStatus) this.liveStatus.textContent = announcement || message;
    }

    renderAppliedFilters() {
      if (!this.applied) return;
      this.applied.querySelectorAll(".pmc__chip").forEach((chip) => chip.remove());
      const fragment = document.createDocumentFragment();

      this.activeFilters.forEach((values, key) => {
        values.forEach((value) => {
          const chip = document.createElement("button");
          chip.type = "button";
          chip.className = "pmc__chip";
          chip.textContent = `${value} ×`;
          chip.setAttribute("aria-label", `Remove ${value} filter`);
          chip.addEventListener("click", () => {
            const selected = this.activeFilters.get(key);
            selected?.delete(value);
            if (!selected?.size) this.activeFilters.delete(key);
            this.syncFilterControls(this.activeFilters);
            this.visibleLimit = VISIBLE_BATCH_SIZE;
            this.commitState(true);
            this.render(true);
          });
          fragment.appendChild(chip);
        });
      });

      this.applied.prepend(fragment);
      this.applied.hidden = this.activeFilters.size === 0;
    }

    readUrlState() {
      const url = new URL(window.location.href);
      const nextFilters = new Map();
      this.facetValues.forEach((allowed, key) => {
        const values = url.searchParams.getAll(`pmc_${key}`).filter((value) => allowed.has(value));
        if (values.length) nextFilters.set(key, new Set(values));
      });
      this.activeFilters = nextFilters;
      const requestedSort = url.searchParams.get(SORT_PARAM);
      if (this.sort && [...this.sort.options].some((option) => option.value === requestedSort)) {
        this.sort.value = requestedSort;
      } else if (this.sort) {
        this.sort.value = "recommended";
      }
    }

    commitState(pushHistory) {
      const url = new URL(window.location.href);
      this.facetValues.forEach((_, key) => url.searchParams.delete(`pmc_${key}`));
      this.activeFilters.forEach((values, key) => {
        values.forEach((value) => url.searchParams.append(`pmc_${key}`, value));
      });
      url.searchParams.delete(SORT_PARAM);
      if (this.sort?.value && this.sort.value !== "recommended") {
        url.searchParams.set(SORT_PARAM, this.sort.value);
      }
      const state = this.interfaceState();
      window.history[pushHistory ? "pushState" : "replaceState"](state, "", url);
    }

    interfaceState() {
      const selectedColors = {};
      this.root.querySelectorAll(".pmc-card[data-product-id]").forEach((card) => {
        if (card.dataset.selectedColor) selectedColors[card.dataset.productId] = card.dataset.selectedColor;
      });
      return {
        ...(window.history.state || {}),
        [HISTORY_KEY]: {
          scrollY: Math.max(0, window.scrollY || 0),
          selectedColors,
          expandedFacets: [...this.mobileExpandedFacets],
        },
      };
    }

    saveInterfaceState() {
      window.history.replaceState(this.interfaceState(), "", window.location.href);
    }

    restoreInterfaceState(state, restoreScroll = false) {
      const saved = state?.[HISTORY_KEY];
      if (!saved) return;
      const expanded = new Set(asArray(saved.expandedFacets));
      this.mobileExpandedFacets = expanded;
      this.filterGroups?.querySelectorAll("[data-filter-group]").forEach((group) => {
        if (this.mobileQuery.matches) {
          group.open = expanded.has(group.dataset.filterGroup) || this.activeFilters.has(group.dataset.filterGroup);
        }
        else {
          group.open = this.activeFilters.has(group.dataset.filterGroup);
        }
      });
      Object.entries(saved.selectedColors || {}).forEach(([id, color]) => {
        this.root.querySelectorAll(`.pmc-card[data-product-id="${id}"]`).forEach((card) => {
          const swatch = [...card.querySelectorAll("button[data-color-name]")]
            .find((button) => button.dataset.colorName === color);
          if (swatch) this.selectSwatch(swatch, false);
        });
      });
      if (restoreScroll && Number.isFinite(saved.scrollY)) {
        window.requestAnimationFrame(() => window.scrollTo({ top: saved.scrollY, behavior: "auto" }));
      }
    }

    adaptToViewport() {
      if (!this.filterGroups || !this.filterOrigin) return;
      if (this.mobileQuery.matches && this.mobileFilterHost) {
        this.mobileFilterHost.appendChild(this.filterGroups);
        this.filterGroups.querySelectorAll("[data-filter-group]").forEach((group) => {
          group.open = this.mobileExpandedFacets.has(group.dataset.filterGroup)
            || this.activeFilters.has(group.dataset.filterGroup);
        });
        if (this.filterShell) this.filterShell.open = false;
      } else {
        this.filterOrigin.parentNode?.insertBefore(this.filterGroups, this.filterOrigin.nextSibling);
        if (this.filterShell) this.filterShell.open = window.innerWidth >= 1280;
        this.filterGroups.querySelectorAll("[data-filter-group]").forEach((group) => {
          group.open = this.activeFilters.has(group.dataset.filterGroup);
        });
        if (this.isDialogOpen(this.filterDialog)) this.closeFilterDialog(false);
        if (this.isDialogOpen(this.sortDialog)) this.closeSortDialog();
      }
    }

    openFilterDialog() {
      this.pendingFilters = cloneFilters(this.activeFilters);
      this.syncFilterControls(this.pendingFilters);
      this.updateMobileFilterPreview();
      this.openDialog(this.filterDialog, this.mobileFilterButton);
    }

    closeFilterDialog(apply) {
      if (!apply) this.syncFilterControls(this.activeFilters);
      this.closeDialog(this.filterDialog);
    }

    openSortDialog() {
      const current = this.sort?.value || "recommended";
      const radio = this.mobileSortOptions?.querySelector(`input[value="${current}"]`);
      if (radio) radio.checked = true;
      this.openDialog(this.sortDialog, this.mobileSortButton);
    }

    closeSortDialog() {
      this.closeDialog(this.sortDialog);
    }

    openDialog(dialog, trigger) {
      if (!dialog) return;
      this.lastDialogTrigger = trigger;
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
      window.requestAnimationFrame(() => {
        const focusTarget = dialog.querySelector("[data-filter-close], [data-sort-close], input, button");
        focusTarget?.focus();
      });
    }

    closeDialog(dialog) {
      if (!dialog || !this.isDialogOpen(dialog)) return;
      if (typeof dialog.close === "function") dialog.close();
      else {
        dialog.removeAttribute("open");
        this.returnDialogFocus();
      }
    }

    isDialogOpen(dialog) {
      return Boolean(dialog?.open || dialog?.hasAttribute("open"));
    }

    returnDialogFocus() {
      const trigger = this.lastDialogTrigger;
      this.lastDialogTrigger = null;
      trigger?.focus();
    }

    trapFallbackFocus(event, dialog) {
      if (event.key !== "Tab" || typeof dialog.showModal === "function") return;
      const focusable = [...dialog.querySelectorAll("button:not([disabled]), input:not([disabled]), a[href]")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    updateMobileFilterPreview() {
      const count = this.filteredProducts(this.pendingFilters).length;
      if (this.mobileResultCount) this.mobileResultCount.textContent = String(count);
      this.updateFilterGroupCounts(this.pendingFilters);
    }

    updateMobileControls() {
      const count = [...this.activeFilters.values()].reduce((total, values) => total + values.size, 0);
      if (this.mobileFilterCount) {
        this.mobileFilterCount.textContent = String(count);
        this.mobileFilterCount.hidden = count === 0;
      }
      const selected = this.sort?.selectedOptions?.[0];
      if (this.mobileSortLabel) this.mobileSortLabel.textContent = selected?.textContent || "Recommended";
    }

    selectSwatch(swatch, saveState) {
      if (swatch.disabled) return;
      const card = swatch.closest(".pmc-card");
      const group = swatch.closest("[data-swatch-group]");
      const image = card?.querySelector("[data-card-image]");
      const link = card?.querySelector("[data-card-link]");
      group?.querySelectorAll("button[data-color-name]").forEach((button) => {
        const selected = button === swatch;
        button.setAttribute("aria-pressed", String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      if (card) card.dataset.selectedColor = swatch.dataset.colorName;
      if (image && swatch.dataset.imageSrc) {
        image.src = swatch.dataset.imageSrc;
        if (swatch.dataset.imageSrcset) image.srcset = swatch.dataset.imageSrcset;
        image.alt = `${card.dataset.productHandle ? card.querySelector(".pmc-card__title")?.textContent.trim() : "Garment"} in ${swatch.dataset.colorName}`;
        image.closest(".pmc-card__media")?.classList.remove("pmc-card__media--failed");
      }
      if (link && swatch.dataset.variantId) {
        const destination = new URL(swatch.dataset.productUrl || link.href, window.location.origin);
        destination.searchParams.set("variant", swatch.dataset.variantId);
        link.href = `${destination.pathname}${destination.search}${destination.hash}`;
      }
      if (saveState) this.saveInterfaceState();
    }

    handleSwatchKeys(event) {
      const current = event.target.closest("[data-swatch-group] button[data-color-name]");
      if (!current || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
      const buttons = [...current.closest("[data-swatch-group]").querySelectorAll("button[data-color-name]:not([disabled])")];
      if (!buttons.length) return;
      event.preventDefault();
      const index = buttons.indexOf(current);
      let next = index;
      if (event.key === "Home") next = 0;
      else if (event.key === "End") next = buttons.length - 1;
      else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = (index - 1 + buttons.length) % buttons.length;
      else next = (index + 1) % buttons.length;
      buttons[next].focus();
      this.selectSwatch(buttons[next], true);
    }

    setUpdating(isUpdating) {
      this.root.dataset.catalogState = isUpdating ? "updating" : "ready";
      if (this.updatingStatus) this.updatingStatus.hidden = !isUpdating;
    }

    showError() {
      this.root.dataset.catalogState = "error";
      if (!this.errorState && this.errorTemplate) {
        const errorState = this.errorTemplate.content.firstElementChild?.cloneNode(true);
        if (errorState) {
          this.errorTemplate.insertAdjacentElement("afterend", errorState);
          this.errorState = errorState;
          this.refreshButton = errorState.querySelector("[data-catalog-refresh]");
          this.refreshButton?.addEventListener("click", () => window.location.reload(), { once: true });
          this.updateContextLinks();
        }
      }
      if (this.liveStatus) this.liveStatus.textContent = "We couldn't load the garments.";
    }

    updateContextLinks() {
      this.root.querySelectorAll("[data-context-link]").forEach((link) => {
        const url = new URL(link.getAttribute("href"), window.location.origin);
        url.searchParams.set("category", this.root.dataset.pluralLabel || "garments");
        url.searchParams.set("collection_url", `${window.location.pathname}${window.location.search}`);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }
  }

  const initialize = () => {
    document.querySelectorAll("[data-garment-collection]").forEach((root) => {
      if (!root.dataset.catalogEnhanced) new GarmentCollection(root);
    });
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialize);
  else initialize();

  document.addEventListener("shopify:section:load", initialize);
})();
